export function StyleGuideSection({
  id,
  title,
  children,
  note,
}: {
  id?: string;
  title: string;
  children: React.ReactNode;
  note?: string;
}) {
  return (
    <section id={id} className="scroll-mt-24 space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-puntt-neutral-gray-9">
        {title}
      </h2>
      {note != null && <StyleGuideNote>{note}</StyleGuideNote>}
      <div className="rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-5">
        {children}
      </div>
    </section>
  );
}

export function StyleGuideNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs leading-relaxed text-puntt-neutral-gray-10">
      {children}
    </p>
  );
}

export function BlockRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-3">{children}</div>;
}

export function BlockPreview({ children }: { children: React.ReactNode }) {
  return <div className="min-w-0 flex-1">{children}</div>;
}

const NAV_GROUPS = [
  {
    label: "Chat status",
    items: [
      { href: "#chat-thinking", label: "ThinkingState" },
      { href: "#chat-agent-steps", label: "AgentSteps" },
      { href: "#chat-search", label: "SearchProgress" },
      { href: "#chat-streaming", label: "ChatMessage streaming" },
      { href: "#chat-structured", label: "StructuredResponse" },
      { href: "#chat-composer", label: "Composer controls" },
      { href: "#chat-suggestions", label: "SuggestedResponses" },
    ],
  },
  {
    label: "Review and project",
    items: [
      { href: "#review-callouts", label: "Review status callouts" },
      { href: "#review-checklist", label: "ReviewChecklist" },
      { href: "#review-action-status", label: "ActionStatus" },
      { href: "#review-stage-bars", label: "Stage progress bars" },
      { href: "#review-ticket-status", label: "Ticket review status" },
      { href: "#review-canvas-staging", label: "Canvas staging" },
      { href: "#review-still-processing", label: "Still processing file" },
      { href: "#review-loading-pane", label: "ProjectLoadingPane" },
    ],
  },
  {
    label: "Canvas blocks",
    items: [{ href: "#canvas-pane", label: "CanvasPane demo" }],
  },
  {
    label: "Adjacent surfaces",
    items: [
      { href: "#adjacent-spinners", label: "Send and upload spinners" },
      { href: "#adjacent-mini-stage", label: "MiniStageBar" },
      { href: "#adjacent-feed", label: "Feed suggestion resolved" },
      { href: "#adjacent-pending", label: "Route pending" },
      { href: "#adjacent-drop-zone", label: "Drop zone overlay" },
      { href: "#adjacent-reviews-loading", label: "Reviews panel loading" },
      { href: "#adjacent-chat-skeleton", label: "Chat history skeleton" },
    ],
  },
  {
    label: "Design tokens",
    items: [{ href: "#design-tokens", label: "Animations and colors" }],
  },
] as const;

export function StyleGuideNav() {
  return (
    <nav
      aria-label="Style guide sections"
      className="sticky top-0 z-10 -mx-8 border-b border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2/95 px-8 py-3 backdrop-blur-sm"
      data-testid="style-guide-nav"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap gap-x-6 gap-y-2">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-puntt-neutral-gray-8">
              {group.label}
            </span>
            {group.items.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`style-guide-nav${item.href.replace("#", "-")}`}
                className="rounded-md px-2 py-0.5 text-xs text-puntt-accent-11 hover:bg-puntt-accent-3"
              >
                {item.label}
              </a>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}
